import { useRef, useState, useLayoutEffect } from 'react';

/**
 * 이력서 미리보기 모달용 스케일러.
 * - position: absolute; inset: 0 으로 부모(relative)를 꽉 채워 definite height 확보
 * - 컨테이너 높이에 맞춰 한 페이지가 딱 보이도록 scale 계산
 * - 세로 스크롤로 다음 페이지 탐색
 *
 * 부모 div에 className="... relative" 또는 style={{ position:'relative' }} 필수
 */
export default function ResumeModalScaler({
  pageW,
  pageH,
  children,
}: {
  pageW: number;
  pageH: number;
  children: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const templateRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.7);
  const [naturalH, setNaturalH] = useState(pageH * 3);

  // 컨테이너 크기 기반으로 scale 계산
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const calc = () => {
      const H = el.clientHeight;
      const W = el.clientWidth;
      if (!H || !W) return;
      setScale(Math.min(H / pageH, W / pageW, 1));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pageW, pageH]);

  // 템플릿의 자연 높이 측정 (transform 영향 없음)
  useLayoutEffect(() => {
    const el = templateRef.current;
    if (el && el.offsetHeight > 0) setNaturalH(el.offsetHeight);
  });

  const scrollH = naturalH * scale;
  const scrollW = pageW * scale;

  return (
    /* position: absolute; inset: 0 → 부모(relative) 크기를 그대로 inherit */
    <div
      ref={containerRef}
      style={{ position: 'absolute', inset: 0, overflow: 'auto' }}
    >
      {/* 스크롤 범위를 올바르게 잡아주는 spacer */}
      <div style={{ height: scrollH, width: scrollW, margin: '0 auto', position: 'relative', flexShrink: 0 }}>
        {/* 원본 크기로 absolute 배치 후 transform으로 시각적 축소 */}
        <div
          ref={templateRef}
          style={{
            position: 'absolute', top: 0, left: 0,
            transformOrigin: 'top left',
            transform: `scale(${scale})`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
